import { PlusIcon } from '@heroicons/react/20/solid'
import {
	ArrowPathIcon,
	ChevronRightIcon,
	InformationCircleIcon,
	MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { yupResolver } from '@hookform/resolvers/yup'
import { ColumnDef } from '@tanstack/react-table'
import {
	ArrowDown,
	ArrowUp,
	Check,
	ChevronDownIcon,
	ChevronsUpDown,
	Eye,
	Loader2,
	MessageCircleMoreIcon,
	MessageCirclePlusIcon,
} from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'

import { format, isAfter, isSameDay, parseISO, subDays } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { enUS } from 'date-fns/locale'
import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'
import * as yup from 'yup'

import { AddSquareIcon, EditIcon, TaskSquareIcon } from '@/assets/icons'
import {
	SalesRecordDialogState,
	SalesRecordsDialog,
	SalesRecordsHistoryDialog,
} from '@/components/sales-records-dialog'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PaginationComponent } from '@/components/ui/pagination'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
	EARLIER_PACKAGES,
	EXTRAS,
	generateRandomPassword,
	PACKAGES,
	primaryIbanOptions,
} from '@/constants'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { capitalizeFirstLetter, formatIban, normalizeIban } from '@/lib/helper'
import { cn } from '@/lib/utils'
import { CreateBookingForm } from '@/pages/admin/bookings-table'
import { applyInvoiceFilterWithAutomaticRevenueRefresh } from '@/pages/admin/invoice-refresh'
import authService from '@/services/auth-service'
import bookingService from '@/services/booking-service'
import userService from '@/services/user-service'
import { updateCounter } from '@/slices/auth'
import { InputMask } from '@react-input/mask'

interface InvoiceForm {
	title: string
	kundenId: number
	firstName: string
	lastName: string
	address: string
	country: string
	email: string
	zipCode: string
	rechnungsnummer: number
	datum: Date
	startDate: Date
	endDate: Date
	paypalCode: string
	amount: number
	discount: number
}

const DEFAULT_INVOICE_PROVISION = 15
const INVOICE_PROVISION_CUTOFF_UTC = Date.UTC(2026, 2, 8, 23, 59, 59, 999)
const EXPLICIT_TIMEZONE_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i

const normalizeBookingOption = (value?: string | null) => value?.trim().toLowerCase() ?? ''

const getCreatedAtTimestampUtc = (createdAt?: string) => {
	if (!createdAt) return null

	const normalizedCreatedAt = EXPLICIT_TIMEZONE_PATTERN.test(createdAt)
		? createdAt
		: `${createdAt}Z`
	const timestamp = Date.parse(normalizedCreatedAt)

	return Number.isNaN(timestamp) ? null : timestamp
}

const getInvoiceProvision = (booking: Booking) => {
	const createdAtTimestamp = getCreatedAtTimestampUtc(booking.createdAt)

	if (createdAtTimestamp === null) {
		return DEFAULT_INVOICE_PROVISION
	}

	const packageOptions =
		createdAtTimestamp <= INVOICE_PROVISION_CUTOFF_UTC ? EARLIER_PACKAGES : PACKAGES
	const selectedExtra = EXTRAS.find(
		extra =>
			normalizeBookingOption(extra.title) === normalizeBookingOption(booking.collect_product_by),
	)
	const packagePrice = Number(booking.original_price) - Number(selectedExtra?.price ?? 0)
	const selectedPackage = packageOptions.find(item => Number(item.price) === packagePrice)

	return selectedPackage?.provision ?? DEFAULT_INVOICE_PROVISION
}

const getInitialInvoiceFilter = () => {
	if (typeof window === 'undefined') {
		return { column: 'user_id', value: '' }
	}

	return {
		column: localStorage.getItem('filterColumn') || 'user_id',
		value: localStorage.getItem('filterValue') || '',
	}
}

export const InvoicesTable = () => {
	const searchableColumns = [
		{ label: 'Buchungs-ID', value: 'id' },
		{ label: 'Regale-ID', value: 'stand_id' },
		{ label: 'User ID', value: 'user_id' },
	]

	const auth = useAppSelector(state => state.auth)
	const dispatch = useAppDispatch()
	const isSmallScreen = useMediaQuery('(max-width: 64rem)')
	const [initialFilter] = useState(getInitialInvoiceFilter)

	const [selectedUserId, setSelectedUserId] = useState<User['id'] | null>(null)
	const [expandedRows, setExpandedRows] = useState<number[]>([])
	const [filterValue, setFilterValue] = useState(initialFilter.value)
	const [isCreateDialogVisible, setIsCreateDialogVisible] = useState(false)
	const [isNoteDialogVisible, setIsNoteDialogVisible] = useState<{
		id: number | null
		note: string
		type: string | null
	}>({
		id: null,
		note: '',
		type: null,
	})
	const [isRefreshDialogVisible, setIsRefreshDetailDialogVisible] = useState(false)
	const [isInvoiceDialogVisible, setIsInvoiceDialogVisible] = useState<Booking | null>(null)
	const [isUserDetailDialogVisible, setIsUserDetailDialogVisible] = useState<User | null>(null)
	const [isSalesRecordDialogVisible, setIsSalesRecordDialogVisible] =
		useState<SalesRecordDialogState>(null)
	const [isSalesRecordHistoryDialogVisible, setIsSalesRecordHistoryDialogVisible] =
		useState<SalesRecordDialogState>(null)
	const [searchValue, setSearchValue] = useState('')
	const [page, setPage] = useState(1)
	const [selectedColumn, setSelectedColumn] = useState(initialFilter.column)
	const [appliedFilterValue, setAppliedFilterValue] = useState(initialFilter.value)
	const [appliedFilterColumn, setAppliedFilterColumn] = useState(initialFilter.column)
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')
	const [open, setOpen] = useState(false)
	const [openCalendar, setOpenCalendar] = useState(false)
	const [openStartCalendar, setOpenStartCalendar] = useState(false)
	const [openEndCalendar, setOpenEndCalendar] = useState(false)

	const schema = yup.object<CreateBookingForm>().shape({
		userType: yup.string().required(),
		existingUser: yup.string().when('userType', {
			is: 'existing',
			then: schema => schema.required('Benutzer ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		rent_price: yup
			.string()
			.test('rent-price-validation', 'Preis ist erforderlich!', function (value) {
				const pckg = this.parent.package
				if (pckg === 'custom' && !value) return false
				return true
			}),
		firstName: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Name ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		lastName: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Name ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		email: yup.string().when('userType', {
			is: 'new',
			then: schema =>
				schema
					.required('E-Mail ist erforderlich')
					.email('Geben Sie eine gültige E-Mail-Adresse ein'),
			otherwise: schema => schema.notRequired(),
		}),
		country: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Ort ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		zip: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Postleitzahl ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		iban: yup.string().when('userType', {
			is: 'new',
			then: schema =>
				schema
					.required('IBAN ist erforderlich!')
					.matches(/^DE(\s?\d){20}$/, "IBAN muss mit 'DE' beginnen und genau 20 Zahlen enthalten"),
			otherwise: schema => schema.notRequired(),
		}),
		address: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Adresse ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		phoneNumber: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Telefonnummer ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		package: yup.string().required('Paket ist erforderlich!'),
		start_date: yup.date().required('Startdatum ist erforderlich!'),
		end_date: yup.date().required('Enddatum ist erforderlich!'),
		shelf: yup.string().required('Regal Nummer ist erforderlich!'),
		additionalServices: yup.string().required('Zusatzleistungen ist erforderlich!'),
	})

	const invoiceSchema = yup.object().shape({
		kundenId: yup
			.number()
			.transform((value, originalValue) => {
				return originalValue === '' ? undefined : value
			})
			.typeError('Die Kunden-Id muss eine Zahl sein')
			.required('Kunden-Id ist erforderlich!'),
		title: yup.string().required('Titel ist erforderlich!'),
		firstName: yup.string().required('Name ist erforderlich!'),
		lastName: yup.string().required('Name ist erforderlich!'),
		address: yup.string().required('Adresse ist erforderlich!'),
		email: yup.string().notRequired(),
		zipCode: yup.string().required('Postleitzahl ist erforderlich!'),
		country: yup.string().required('Ort ist erforderlich!'),
		rechnungsnummer: yup
			.number()
			.transform((value, originalValue) => {
				return originalValue === '' ? undefined : value
			})
			.typeError('Die Rechnungsnummer muss eine Zahl sein')
			.required('Rechnungsnummer ist erforderlich!'),
		datum: yup.date().required('Datum ist erforderlich!'),
		startDate: yup.date().required('Datum ist erforderlich!'),
		endDate: yup.date().required('Datum ist erforderlich!'),
		paypalCode: yup.string().notRequired(),
		amount: yup.string().required('Betrag ist erforderlich!'),
		discount: yup
			.number()
			.transform((value, originalValue) => {
				return originalValue === '' ? undefined : value
			})
			.typeError('Die Provision muss eine Zahl sein')
			.required('Provision ist erforderlich!'),
	})

	const form = useForm<CreateBookingForm>({
		resolver: yupResolver(schema as any),
		defaultValues: { userType: 'new' },
		mode: 'all',
	})

	const invoiceForm = useForm<InvoiceForm>({
		resolver: yupResolver(invoiceSchema as any),
		mode: 'all',
	})

	const { data, refetch, isLoading } = useQuery(
		['fetch-bookings', page, rowsPerPage, appliedFilterColumn, appliedFilterValue],
		() =>
			bookingService.fetchBookingsData({
				pageNumber: page.toString(),
				pageSize: rowsPerPage.toString(),
				filterColumn: appliedFilterColumn,
				filterValue: appliedFilterValue,
			}),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching bookings')
			},
		},
	)

	const startDate = form.watch('start_date')
	const endDate = form.watch('end_date')

	const { data: users } = useQuery('fetch-users', () => userService.fetchAllUsers(), {
		refetchOnWindowFocus: false,
		enabled: isRefreshDialogVisible || form.watch('userType') === 'existing',
		onError: () => {
			toast.error('Error fetching users')
		},
	})

	const { data: holidays } = useQuery('fetch-holidays', () => bookingService.fetchHolidays(), {
		enabled: isCreateDialogVisible,
		refetchOnWindowFocus: false,
		onError: () => toast.error('Error fetching holidays'),
	})

	const { mutate, data: standList } = useMutation({
		mutationKey: 'fetch-stands',
		mutationFn: ({
			startDate,
			endDate,
		}: {
			startDate: Booking['start']
			endDate: Booking['end']
		}) => bookingService.fetchStandsData(startDate, endDate),
	})

	const { mutate: registerUser } = useMutation({
		mutationKey: 'register-user',
		mutationFn: (data: any) => authService.registerUser(data),
	})

	const { mutate: placeOrder } = useMutation({
		mutationKey: 'place-order',
		mutationFn: (data: any) => bookingService.placeAdminOrder(data),
	})

	const { mutate: sendBookingEmail, isLoading: placingBooking } = useMutation({
		mutationKey: 'send-email',
		mutationFn: (data: any) => bookingService.sendBookingEmail(data),
	})

	const { mutate: generateInvoice, isLoading: generatingInvoice } = useMutation({
		mutationKey: 'generate-invoice',
		mutationFn: (data: Invoice) => bookingService.generateInvoice(data),
	})

	const { mutate: verifyEmail } = useMutation({
		mutationKey: 'verify-email',
		mutationFn: (data: { email: string }) => authService.verifyEmail(data),
	})

	const { mutate: updateNote, isLoading: isUpdatingNote } = useMutation({
		mutationKey: 'update-note',
		mutationFn: ({
			note,
			id,
		}: {
			note: { user_notes: string } | { booking_notes: string }
			id: number
		}) => bookingService.updateNote(note, id),
		onSuccess: () => {
			toast.success('Hinweis erfolgreich aktualisiert')
			setIsNoteDialogVisible({ note: '', id: null, type: null })
			refetch()
		},
		onError: (err: any) => {
			toast.error(
				err?.response?.data?.message ??
					'Hinweis konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
			)
		},
	})

	const { mutate: updatePaymentStatus } = useMutation({
		mutationKey: 'update-payment-status',
		mutationFn: (data: { id: number; status: string }) =>
			bookingService.updatePaymentStatus(data.id, data.status),
	})

	const {
		mutate: updateCustomerBooking,
		mutateAsync: updateCustomerBookingAndWait,
		isLoading: updatingCustomerBooking,
	} = useMutation({
		mutationKey: 'update-customer-booking',
		mutationFn: (id: number) => bookingService.updateCustomerBooking(id),
		onSuccess: () => {
			refetch()
			toast.success('Aktualisierung erfolgreich')
		},
	})

	useEffect(() => {
		setPage(1)
	}, [rowsPerPage, appliedFilterColumn, appliedFilterValue])

	useEffect(() => {
		if (startDate && endDate) {
			mutate({ startDate: format(startDate, 'yyyy-MM-dd'), endDate: format(endDate, 'yyyy-MM-dd') })
		}
	}, [startDate, endDate])

	const handleSalesRecordDialogOpen = (booking: Booking) => {
		setIsSalesRecordDialogVisible({
			bookingId: booking.id,
			userId: booking.user_id,
			userName: `${booking.user.first_name} ${booking.user.last_name}`.trim(),
		})
	}

	const handleSalesRecordHistoryDialogOpen = (booking: Booking) => {
		setIsSalesRecordHistoryDialogVisible({
			bookingId: booking.id,
			userId: booking.user_id,
			userName: `${booking.user.first_name} ${booking.user.last_name}`.trim(),
		})
	}

	const sortedData = useMemo(() => {
		if (!data) return []
		if (!sortColumn) return data.data

		return [...data.data].sort((a, b) => {
			const aValue = a[sortColumn as keyof Booking]
			const bValue = b[sortColumn as keyof Booking]

			if (aValue == null || bValue == null) return 0

			let aVal: any = aValue
			let bVal: any = bValue

			if (aVal instanceof Date) aVal = aVal.getTime()
			if (bVal instanceof Date) bVal = bVal.getTime()

			const aParsed = typeof aVal === 'string' ? Date.parse(aVal) : NaN
			const bParsed = typeof bVal === 'string' ? Date.parse(bVal) : NaN

			if (!isNaN(aParsed) && !isNaN(bParsed)) {
				aVal = aParsed
				bVal = bParsed
			}

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
			}

			return 0
		})
	}, [data, sortColumn, sortDirection])

	const { filteredBookings, isSingleUser } = useMemo(() => {
		const filteredBookings =
			sortedData?.filter(curr => {
				if (!searchValue) {
					return true
				}
				const searchLowerCase = searchValue.toLowerCase()
				return (
					curr?.user?.id?.toString().includes(searchLowerCase) ||
					curr?.stand_id?.toString().includes(searchLowerCase)
				)
			}) || []

		const uniqueUserIds = new Set(filteredBookings.map(b => b?.user?.id))
		const isSingleUser = uniqueUserIds.size === 1

		return { filteredBookings, isSingleUser }
	}, [sortedData, searchValue])

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

	const handleApplyFilter = async () => {
		const nextFilterValue = filterValue.trim()
		const applyFilter = () => {
			localStorage.setItem('filterValue', nextFilterValue)
			localStorage.setItem('filterColumn', selectedColumn)
			setFilterValue(nextFilterValue)
			setPage(1)
			setAppliedFilterColumn(selectedColumn)
			setAppliedFilterValue(nextFilterValue)
		}

		try {
			await applyInvoiceFilterWithAutomaticRevenueRefresh({
				filterColumn: selectedColumn,
				filterValue: nextFilterValue,
				refreshRevenue: updateCustomerBookingAndWait,
				applyFilter,
			})
		} catch {
			toast.error('Umsätze konnten nicht aktualisiert werden')
		}
	}

	const handleNoteSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		const formData = new FormData(event.currentTarget)
		const formJson = Object.fromEntries((formData as any).entries())
		const note = String(formJson.note ?? '')
		if (isNoteDialogVisible.id) {
			if (isNoteDialogVisible.type === 'booking') {
				updateNote({ note: { booking_notes: note }, id: isNoteDialogVisible.id })
			} else {
				updateNote({ note: { user_notes: note }, id: isNoteDialogVisible.id })
			}
		}
	}

	const handlePaymentStatus = async (id: number, status: string) => {
		updatePaymentStatus(
			{ id, status },
			{
				onSuccess: () => {
					toast.success('Payment status updated successfully')
					refetch()
				},
				onError: () => {
					toast.error('Failed to update payment status. Please try again')
				},
			},
		)
	}

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const calcWorkingDays = (startDate: Date, days: number) => {
		let count = 1
		if (!holidays || !startDate) return

		const workingDate = new Date(startDate)

		while (count < days) {
			workingDate.setDate(workingDate.getDate() + 1)
			if (
				workingDate.getDay() !== 0 &&
				!holidays.data?.find(date => isSameDay(parseISO(date.holiday_date), workingDate))
			) {
				count++
			}
		}
		return workingDate
	}

	const selectDayItem = PACKAGES.find(pkg => pkg.id === Number(form.watch('package')))

	useEffect(() => {
		if (startDate) {
			const endDate = calcWorkingDays(startDate, selectDayItem ? selectDayItem.duration : 0)
			if (endDate) {
				form.setValue('end_date', endDate)
			}
		}
	}, [startDate, selectDayItem])

	const formatNumber = (value: number) => {
		return new Intl.NumberFormat('de-DE', {
			style: 'decimal',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(value)
	}

	const onSubmit = (data: CreateBookingForm) => {
		let finalPrice = 0
		let calculatedDuration = null
		let selectedPackage = null
		let selectedExtra = null

		if (data.package === 'custom') {
			finalPrice = parseFloat(data.rent_price as string)
			calculatedDuration =
				Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1

			if (calculatedDuration <= 0) {
				toast.error('End date must be later than start date.')
				return
			}
		} else {
			selectedPackage = PACKAGES.find(item => item.id === Number(data.package))
			selectedExtra = EXTRAS.find(item => item.id === Number(data.additionalServices))

			if (!selectedPackage) {
				toast.error('Invalid package selected.')
				return
			}

			const extraPrice = selectedExtra ? Number(selectedExtra.price) : 0

			finalPrice = Number(selectedPackage.price) + extraPrice
		}

		if (data.userType === 'new') {
			verifyEmail(
				{ email: data.email },
				{
					onSuccess: res => {
						if (res?.data?.message === 'User exist with this email') {
							toast.error('User with this email already exists!')
							return
						} else {
							const passwordString = generateRandomPassword(10)

							const payload = {
								...data,
								iban: normalizeIban(data.iban),
								first_name: data.firstName,
								last_name: data.lastName,
								status: 'active',
								password: passwordString,
								confirm_password: passwordString,
								news_offer: true,
							}

							registerUser(payload, {
								onSuccess: res => {
									if (res?.data?.status) {
										const bookingPayload = {
											shelf_type: 1,
											stand_id: Number(data.shelf),
											duration:
												data.package === 'custom' ? calculatedDuration : selectDayItem?.duration,
											start: format(data.start_date, 'yyyy-MM-dd'),
											end: format(data.end_date, 'yyyy-MM-dd'),
											collect_product_by: selectedExtra?.title,
											rebook: 0,
											rent_price: finalPrice,
											original_price: finalPrice,
											status: 'active',
											prepare: format(subDays(data.start_date, 1), 'yyyy-MM-dd', { locale: enUS }),
											user_id: res?.data?.id,
											description: ' ',
											coupon: 'null',
											referral_code: null,
										}

										placeOrder(bookingPayload, {
											onSuccess: () => {
												sendEmail(
													data.package === 'custom' ? 'Custom' : selectDayItem!,
													selectedExtra!,
													data,
													res?.data,
												)
											},
											onError: (error: any) => {
												toast.error(
													error?.response?.data?.message ?? error?.request ?? error?.message,
												)
											},
										})
									}
								},
							})
						}
					},
				},
			)
		} else {
			const bookingPayload = {
				shelf_type: 1,
				stand_id: Number(data.shelf),
				duration: data.package === 'custom' ? calculatedDuration : selectDayItem?.duration,
				start: format(data.start_date, 'yyyy-MM-dd'),
				end: format(data.end_date, 'yyyy-MM-dd'),
				collect_product_by: selectedExtra?.title,
				rent_price: finalPrice,
				original_price: finalPrice,
				status: 'active',
				prepare: format(subDays(data.start_date, 1), 'yyyy-MM-dd', { locale: enUS }),
				user_id: Number(data.existingUser),
				description: ' ',
				coupon: 'null',
				referral_code: null,
			}
			placeOrder(bookingPayload, {
				onSuccess: () => {
					sendEmail(
						data.package === 'custom' ? 'Custom' : selectDayItem!,
						selectedExtra!,
						data,
						users?.find(user => user.id === Number(data.existingUser)) as User,
					)
				},
				onError: (error: any) => {
					toast.error(error?.response?.data?.message ?? error?.request ?? error?.message)
				},
			})
		}
	}

	const onInvoiceSubmit = (data: InvoiceForm) => {
		const cleanedAmountString = (data.amount as any)
			.replace(/[^\d,.-]/g, '')
			.replace(/\./g, '')
			.replace(',', '.')

		const salesCommission = Number(cleanedAmountString) * Number(data.discount / 100)

		const payload = JSON.stringify({
			customerNumber: data.kundenId,
			sureName: capitalizeFirstLetter(data.firstName),
			familyname: capitalizeFirstLetter(data.lastName),
			street: data.address,
			customerEmail: data.email,
			zip: data.zipCode,
			city: data.country,
			invoice_invoiceNumber: data.rechnungsnummer,
			invoice_invoiceDate: format(data.datum, 'dd.MM.yyyy'),
			invoice_headText: `<p>Hallo ${data.firstName},</p><p>während deiner Mietlaufzeit vom ${format(
				data?.startDate,
				'dd.MM.yyyy',
			)} - ${format(data?.endDate, 'dd.MM.yyyy')} hast du einen Gesamtumsatz von <b>${formatNumber(
				Number(cleanedAmountString),
			)}</b>€ erzielt.&nbsp;</p><p>Wir&nbsp;überweisen dir den Gesamtumsatz abzüglich der vereinbarten Verkaufsprovision i.H.v. ${
				data.discount
			} % auf die von dir genannte&nbsp;Bankverbindung.</p>`,
			invoice_footText: `<p>Der Auszahlungsbetrag i.H.v. <b>${formatNumber(
				Number(cleanedAmountString) - salesCommission,
			)}</b>€ wird dir in den kommenden Werktagen überwiesen.</p><p>Vielen Dank für deine Buchung und bis bald!</p><p>Dein Team von minmini</p>`,
			invoice_address: `${data.firstName} ${data.lastName}\nIm Brauereiviertel 16\n24118 Kiel`,
			invoice_payDate: format(data.datum, 'dd.MM.yyyy'),
			invoice_deliveryDate: format(data.datum, 'dd.MM.yyyy'),
			invoice_sendDate: format(data.datum, 'dd.MM.yyyy'),
			invoice_customerInternalNote: data.paypalCode,
			invoicePosSave_0_quantity: (Number(data.discount) / 100).toFixed(2).toString(),
			invoicePosSave_0_price: data.amount,
			invoicePosSave_0_name: `Provision ${data.discount}%`,
			invoicePosSave_0_text: `Provision ${data.discount}%`,
			invoicePosSave_0_priceGross: cleanedAmountString,
			total_price: Number(cleanedAmountString),
			booking_id: isInvoiceDialogVisible?.id,
			user_id: isInvoiceDialogVisible?.user_id,
		})

		generateInvoice(payload as any, {
			onSuccess: response => {
				toast.success(response?.message)
				dispatch(updateCounter(response?.updatedCounter))
				invoiceForm.reset()
				setIsInvoiceDialogVisible(null)
			},
			onError: () => {
				toast.error('Failed to generate invoice. Please try again')
			},
		})
	}

	const sendEmail = async (
		selectDayItem: (typeof PACKAGES)[0] | string,
		extraItem: (typeof EXTRAS)[0],
		values: CreateBookingForm,
		data: User,
	) => {
		const payload = {
			bookingStand: typeof selectDayItem === 'string' ? selectDayItem : selectDayItem?.title,
			userDetails: data,
			selectedStartDate: values.start_date,
			selectedEndDate: values.end_date,
			selectedPackage: selectDayItem,
			extraItem: extraItem,
			selectedDayItem: selectDayItem,
			selectedStand: standList?.find(stand => stand.id === Number(values.shelf)),
			newsOffer: true,
		}

		sendBookingEmail(payload, {
			onSuccess: () => {
				toast.success('Booking has been created and confirmation email has been sent!')
				refetch()
				form.reset()
				setIsCreateDialogVisible(false)
			},
			onError: () => {
				toast.error('Booking has been created but confirmation email could not be sent!')
			},
		})
	}

	const columns: ColumnDef<Booking>[] = [
		{
			accessorKey: 'id',
			meta: {
				title: 'Buchungs ID',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Buchungs-ID" />,
			cell: ({ row }) => {
				const createdDate = new Date(row.original.createdAt)
				const isAfter8March = isAfter(
					createdDate.setHours(0, 0, 0, 0),
					new Date('2026-03-08').setHours(0, 0, 0, 0),
				)
				return (
					<div className="flex">
						<div
							className={cn('bg-yellow-200/80 p-1.5 rounded-[18px] w-fit px-3', {
								'bg-purple-200/80': isAfter8March,
							})}>
							{row.getValue('id')}
						</div>
						<div
							onClick={() =>
								setIsNoteDialogVisible({
									note: row.original.booking_notes ?? '',
									id: row.getValue('id'),
									type: 'booking',
								})
							}
							className="cursor-pointer relative rounded-[18px] w-fit pl-1">
							<MessageCircleMoreIcon className="size-4" />
							<div
								className={cn('size-2 rounded-full bg-red-500 absolute top-0 -right-1', {
									hidden: row.original.booking_notes === null || row.original.booking_notes === '',
								})}
							/>
						</div>
					</div>
				)
			},
		},
		{
			accessorKey: 'stand_id',
			meta: {
				title: 'Regale-ID',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Regale-ID" />,
		},
		{
			accessorKey: 'user_id',
			meta: {
				title: 'User ID',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="User ID" />,
			cell: ({ row }) => (
				<div className="flex items-center">
					<div
						onClick={() => handleSalesRecordDialogOpen(row.original)}
						className="cursor-pointer rounded-[18px] w-fit pr-1">
						<MessageCirclePlusIcon className="size-4" />
					</div>
					<div
						onClick={() => handleSalesRecordHistoryDialogOpen(row.original)}
						className="cursor-pointer rounded-[18px] w-fit pr-1">
						<Eye className="size-4" />
					</div>
					<div
						onClick={() => setIsUserDetailDialogVisible(row.original.user)}
						className="bg-green-200/80 cursor-pointer p-1.5 rounded-[18px] w-fit px-3">
						{row.getValue('user_id')}
					</div>
					<div
						onClick={() =>
							setIsNoteDialogVisible({
								note: row.original.user_notes ?? '',
								id: row.getValue('id'),
								type: 'user',
							})
						}
						className="cursor-pointer relative rounded-[18px] w-fit pl-1">
						<MessageCircleMoreIcon className="size-4" />
						<div
							className={cn('size-2 rounded-full bg-red-500 absolute top-0 -right-1', {
								hidden: row.original.user_notes === null || row.original.user_notes === '',
							})}
						/>
					</div>
				</div>
			),
		},
		{
			accessorKey: 'paypal_code',
			meta: {
				title: 'PayPal Code',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="PayPal Code" />,
		},
		{
			accessorKey: 'rent_price',
			meta: {
				title: 'Preis',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Preis" />,
		},
		{
			accessorKey: 'total_earnings',
			meta: {
				title: 'Gesamteinnahmen',
			},
			accessorFn: (row: Booking) => `€${row.total_earnings}`,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Gesamteinnahmen" />,
		},
		{
			accessorKey: 'total_quantities',
			meta: {
				title: 'Gesamtanzahl',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Gesamtanzahl" />,
		},
		{
			accessorKey: 'start',
			meta: {
				title: 'Startdatum',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Startdatum" />,
			cell: ({ row }) => (
				<div>{format(toZonedTime(new Date(row.getValue('start')), 'UTC'), 'dd-MM-yyyy')}</div>
			),
		},
		{
			accessorKey: 'end',
			meta: {
				title: 'Enddatum',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Enddatum" />,
			cell: ({ row }) => (
				<div>{format(toZonedTime(new Date(row.getValue('end')), 'UTC'), 'dd-MM-yyyy')}</div>
			),
		},
		{
			accessorKey: 'is_paid',
			meta: {
				title: 'Status',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
			cell: ({ row }) => (
				<div
					className={cn(
						'w-fit rounded-[18px] p-1.5 px-3',
						row.getValue('is_paid')
							? 'bg-[#61B9641A] text-[#61B964]'
							: 'bg-[#FFEFF2] text-[#DB5358]',
					)}>
					{row.getValue('is_paid') ? 'Bezahlt' : 'Unbezahlt'}
				</div>
			),
		},
		{
			id: 'action-invoices',
			enableSorting: false,
			enableHiding: false,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Aktionen" />,
			cell: ({ row }) => (
				<div className="flex items-center justify-center gap-0.5">
					<Tooltip>
						<TooltipTrigger
							onClick={() =>
								handlePaymentStatus(row.original.id, row.original.is_paid ? 'unpaid' : 'paid')
							}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<EditIcon className="shrink-0" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Aktualisieren</p>
						</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger
							onClick={() => {
								let parsed = parseFloat(
									row.original?.total_earnings?.replace(/[^\d.-]/g, '').replace(',', '.'),
								)
								let parsedAmount = ''
								if (!isNaN(parsed)) {
									const formatted = new Intl.NumberFormat('de-DE', {
										style: 'currency',
										currency: 'EUR',
										minimumFractionDigits: 2,
									}).format(parsed)
									parsedAmount = formatted
								} else {
									parsedAmount = row.original?.total_earnings
								}
								invoiceForm.reset({
									title: 'Liebe',
									email: row.original?.user?.email ? row.original?.user?.email : '',
									firstName: row.original?.user?.first_name
										? row.original.user.first_name.charAt(0).toUpperCase() +
											row.original.user.first_name.slice(1)
										: '',
									lastName: row.original?.user?.last_name
										? row.original.user.last_name.charAt(0).toUpperCase() +
											row.original.user.last_name.slice(1)
										: '',
									address: row.original?.user?.address
										? row.original.user.address.replace(/(?<=^|[\s-])\w/g, char =>
												char.toUpperCase(),
											)
										: '',
									zipCode: row.original?.user?.zip
										? row.original.user.zip.charAt(0).toUpperCase() + row.original.user.zip.slice(1)
										: '',
									country: row.original?.user?.country
										? row.original.user.country.charAt(0).toUpperCase() +
											row.original.user.country.slice(1)
										: '',
									kundenId: row.original?.user_id,
									datum: new Date(),
									startDate: new Date(row.original.start),
									endDate: new Date(row.original.end),
									paypalCode:
										Number(row.original?.rent_price) === 0
											? 'Mietfrei' + (row.original?.paypal_code || '')
											: row.original?.paypal_code
												? row.original?.paypal_code
												: '',
									rechnungsnummer: auth.invoice_counter,
									discount: getInvoiceProvision(row.original),
									amount: parsedAmount as any,
								})
								setIsInvoiceDialogVisible(row.original)
							}}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<AddSquareIcon className="shrink-0" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Generieren</p>
						</TooltipContent>
					</Tooltip>
				</div>
			),
		},
	]

	const MobileBookingsTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('id')}
							className="text-center cursor-pointer text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Buchungs ID
								{sortColumn === 'id' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('status')}
							className="text-center cursor-pointer text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Status
								{sortColumn === 'status' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('end')}
							className="text-center cursor-pointer text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Enddatum
								{sortColumn === 'end' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{filteredBookings?.map((booking, index) => (
						<React.Fragment key={booking.id.toString() + index.toString()}>
							<TableRow
								className={cn(
									'h-14',
									expandedRows.includes(booking.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
								)}
								onClick={() => toggleRow(booking.id)}>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(booking.id),
									})}>
									<div className="flex items-center">
										<ChevronRightIcon
											className={cn(
												'size-3 shrink-0 -mt-0.5',
												expandedRows.includes(booking.id)
													? 'rotate-90 text-secondary'
													: 'text-muted-foreground',
											)}
										/>
										<div
											className={cn('bg-yellow-200/80 px-1 py-0.5 rounded-[18px] w-fit', {
												'bg-purple-200/80': isAfter(
													new Date(booking.createdAt).setHours(0, 0, 0, 0),
													new Date('2026-03-08').setHours(0, 0, 0, 0),
												),
											})}>
											{booking.id}
										</div>
										<div
											onClick={() =>
												setIsNoteDialogVisible({
													note: booking.booking_notes ?? '',
													type: 'booking',
													id: booking.id,
												})
											}
											className="cursor-pointer relative rounded-[18px] w-fit pl-1">
											<MessageCircleMoreIcon className="size-3" />
											<div
												className={cn('size-1.5 rounded-full bg-red-500 absolute top-0 -right-1', {
													hidden: booking.booking_notes === null || booking.booking_notes === '',
												})}
											/>
										</div>
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-[10px]', {
										'text-muted-foreground': !expandedRows.includes(booking.id),
									})}>
									<div
										className={cn(
											'text-[10px] w-fit p-1.5 px-2 rounded-[18px]',
											booking.is_paid
												? 'bg-[#61B9641A] text-[#61B964]'
												: 'bg-[#FFEFF2] text-[#DB5358]',
										)}>
										{booking.is_paid ? 'Bezahlt' : 'Unbezahlt'}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(booking.id),
									})}>
									{format(toZonedTime(new Date(booking.end), 'UTC'), 'dd-MM-yyyy')}
								</TableCell>
							</TableRow>
							{expandedRows.includes(booking.id) && (
								<TableRow
									className={cn(
										'mt-2',
										!expandedRows.includes(booking.id)
											? '!border-b-0'
											: 'border-b border-[#F4F4F4]',
									)}>
									<TableCell className="pt-0" colSpan={3}>
										<div className="px-3 flex flex-col gap-4">
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Startdatum
												</h6>
												<p className="text-secondary text-xs font-medium">
													{format(toZonedTime(new Date(booking.start), 'UTC'), 'dd-MM-yyyy')}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Enddatum</h6>
												<p className="text-secondary text-xs font-medium">
													{format(toZonedTime(new Date(booking.end), 'UTC'), 'dd-MM-yyyy')}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Regale-ID</h6>
												<p className="text-secondary text-xs font-medium">{booking.stand_id}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">User ID</h6>
												<div className="flex items-center gap-2">
													<p
														onClick={() => handleSalesRecordDialogOpen(booking)}
														className="text-secondary bg-green-200/80 p-1.5 px-3 rounded-[18px] w-fit cursor-pointer text-xs font-medium">
														<MessageCirclePlusIcon className="size-3" />
													</p>
													<p
														onClick={() => handleSalesRecordHistoryDialogOpen(booking)}
														className="text-secondary bg-green-200/80 p-1.5 px-3 rounded-[18px] w-fit cursor-pointer text-xs font-medium">
														<Eye className="size-3" />
													</p>
													<p
														onClick={() => setIsUserDetailDialogVisible(booking.user)}
														className="text-secondary bg-green-200/80 p-1.5 px-3 rounded-[18px] w-fit cursor-pointer text-xs font-medium">
														{booking.user_id}
													</p>
													<p
														onClick={() =>
															setIsNoteDialogVisible({
																note: booking.user_notes ?? '',
																id: booking.id,
																type: 'user',
															})
														}
														className="text-secondary relative bg-green-200/80 p-1.5 px-3 rounded-[18px] w-fit cursor-pointer text-xs font-medium">
														<MessageCircleMoreIcon className="size-3" />
														<div
															className={cn(
																'size-1.5 rounded-full bg-red-500 absolute top-1 right-2',
																{
																	hidden: booking.user_notes === null || booking.user_notes === '',
																},
															)}
														/>
													</p>
												</div>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													PayPal Code
												</h6>
												<p className="text-secondary text-xs font-medium">{booking.paypal_code}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Preis</h6>
												<p className="text-secondary text-xs font-medium">{booking.rent_price}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Gesamteinnahmen
												</h6>
												<p className="text-secondary text-xs font-medium">
													{booking.total_earnings}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Gesamtanzahl
												</h6>
												<p className="text-secondary text-xs font-medium">
													{booking.total_quantities}
												</p>
											</div>

											<div className="flex items-center justify-between">
												<div className="flex items-center gap-1">
													<InformationCircleIcon className="text-muted-foreground size-3" />
													<h6 className="text-muted-foreground text-[10px] font-medium">Aktion</h6>
												</div>
												<div className="flex items-center gap-1">
													<div
														onClick={() =>
															handlePaymentStatus(booking.id, booking.is_paid ? 'unpaid' : 'paid')
														}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<EditIcon className="shrink-0 max-lg:size-3" />
													</div>
													<div
														onClick={() => {
															let parsed = parseFloat(
																booking?.total_earnings.replace(/[^\d.-]/g, '').replace(',', '.'),
															)
															let parsedAmount = ''
															if (!isNaN(parsed)) {
																const formatted = new Intl.NumberFormat('de-DE', {
																	style: 'currency',
																	currency: 'EUR',
																	minimumFractionDigits: 2,
																}).format(parsed)
																parsedAmount = formatted
															} else {
																parsedAmount = booking?.total_earnings
															}
															invoiceForm.reset({
																title: 'Liebe',
																email: booking?.user?.email ? booking?.user?.email : '',
																firstName: booking?.user?.first_name
																	? booking.user.first_name.charAt(0).toUpperCase() +
																		booking.user.first_name.slice(1)
																	: '',
																lastName: booking?.user?.last_name
																	? booking.user.last_name.charAt(0).toUpperCase() +
																		booking.user.last_name.slice(1)
																	: '',
																address: booking?.user?.address
																	? booking.user.address.replace(/(?<=^|[\s-])\w/g, char =>
																			char.toUpperCase(),
																		)
																	: '',
																zipCode: booking?.user?.zip
																	? booking.user.zip.charAt(0).toUpperCase() +
																		booking.user.zip.slice(1)
																	: '',
																country: booking?.user?.country
																	? booking.user.country.charAt(0).toUpperCase() +
																		booking.user.country.slice(1)
																	: '',
																kundenId: booking?.user_id,
																datum: new Date(),
																startDate: new Date(booking.start),
																endDate: new Date(booking.end),
																paypalCode:
																	Number(booking?.rent_price) === 0
																		? 'Mietfrei' + (booking?.paypal_code || '')
																		: booking?.paypal_code
																			? booking?.paypal_code
																			: '',
																rechnungsnummer: auth.invoice_counter,
																discount: getInvoiceProvision(booking),
																amount: parsedAmount as any,
															})
															setIsInvoiceDialogVisible(booking)
														}}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<AddSquareIcon className="shrink-0 max-lg:size-3" />
													</div>
												</div>
											</div>
										</div>
									</TableCell>
								</TableRow>
							)}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		)
	}

	return (
		<div className="rounded-[20px] bg-white max-lg:p-4 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
			<SalesRecordsDialog
				dialogState={isSalesRecordDialogVisible}
				onClose={() => setIsSalesRecordDialogVisible(null)}
				onSuccess={() => refetch()}
			/>
			<SalesRecordsHistoryDialog
				dialogState={isSalesRecordHistoryDialogVisible}
				onClose={() => setIsSalesRecordHistoryDialogVisible(null)}
				onSuccess={() => refetch()}
			/>
			<ResponsiveModal
				open={isNoteDialogVisible.id !== null}
				onOpenChange={() => setIsNoteDialogVisible({ note: '', id: null, type: null })}>
				<ResponsiveModalContent
					crossIconClassName="top-3 lg:top-4.5"
					className="!px-6 !py-4 !max-w-[550px] gap-0">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							{isNoteDialogVisible.type === 'booking' ? 'Buchungshinweis' : 'Benutzerhinweis'}
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							{isNoteDialogVisible.type === 'booking' ? 'Buchungshinweis' : 'Benutzerhinweis'}
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<form onSubmit={handleNoteSubmit} className="mt-3 lg:mt-4 flex flex-col">
						<Textarea
							defaultValue={isNoteDialogVisible.note}
							className="resize-none max-lg:placeholder:text-sm min-h-[117px]"
							name="note"
							placeholder="Notiz hinzufügen"
						/>
						<Button
							variant="secondary"
							className="mt-6 w-full lg:h-[44px] rounded-[12px] font-extrabold text-white">
							{isUpdatingNote && <Loader2 className="animate-spin" />}
							{isUpdatingNote ? 'Bitte warten' : 'Speichern'}
						</Button>
					</form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={isInvoiceDialogVisible !== null}
				onOpenChange={() => {
					setIsInvoiceDialogVisible(null)
				}}>
				<ResponsiveModalContent className="gap-0">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Rechnungsdetails aktualisieren
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Rechnungsdetails aktualisieren
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...invoiceForm}>
						<form onSubmit={invoiceForm.handleSubmit(onInvoiceSubmit)}>
							<div className="grid grid-cols-1 lg:grid-cols-3 mt-6 gap-y-6 lg:mt-10 lg:gap-x-4 lg:gap-y-5">
								<FormField
									control={invoiceForm.control}
									name="title"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Titel <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Titel" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="firstName"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Vorname <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="First Name"
													className="capitalize"
													{...field}
													value={capitalizeFirstLetter(field.value)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="lastName"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Name <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Last Name"
													className="capitalize"
													{...field}
													value={capitalizeFirstLetter(field.value)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="address"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Anschrift <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Address" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="zipCode"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Postleitzahl <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Zip Code" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="country"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Ort <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Country" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="kundenId"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Kunden - ID <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Kunden - ID" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="datum"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Rechnungsdatum <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<Popover open={openCalendar} onOpenChange={setOpenCalendar}>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant={'outline'}
															className={cn(
																'w-full max-lg:text-xs hover:bg-white px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																!field.value && 'text-muted-foreground',
															)}>
															{field.value ? (
																format(field.value, 'dd.MM.yyyy')
															) : (
																<span>dd.mm.yyyy</span>
															)}
															<ChevronDownIcon className="size-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="p-0 border-input" align="start">
													<Calendar
														mode="single"
														selected={field.value}
														onSelect={val => {
															field.onChange(val)
															setOpenCalendar(false)
														}}
														initialFocus
													/>
												</PopoverContent>
											</Popover>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="startDate"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Startdatum <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<Popover open={openStartCalendar} onOpenChange={setOpenStartCalendar}>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant={'outline'}
															className={cn(
																'w-full max-lg:text-xs hover:bg-white px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																!field.value && 'text-muted-foreground',
															)}>
															{field.value ? (
																format(field.value, 'dd.MM.yyyy')
															) : (
																<span>dd.mm.yyyy</span>
															)}
															<ChevronDownIcon className="size-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="p-0 border-input" align="start">
													<Calendar
														mode="single"
														selected={field.value}
														onSelect={val => {
															field.onChange(val)
															setOpenStartCalendar(false)
														}}
														initialFocus
													/>
												</PopoverContent>
											</Popover>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="endDate"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Enddatum <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<Popover open={openEndCalendar} onOpenChange={setOpenEndCalendar}>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant={'outline'}
															className={cn(
																'w-full hover:bg-white max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																!field.value && 'text-muted-foreground',
															)}>
															{field.value ? (
																format(field.value, 'dd.MM.yyyy')
															) : (
																<span>dd.mm.yyyy</span>
															)}
															<ChevronDownIcon className="size-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="w-auto p-0 border-input" align="start">
													<Calendar
														mode="single"
														selected={field.value}
														onSelect={val => {
															field.onChange(val)
															setOpenEndCalendar(false)
														}}
														initialFocus
													/>
												</PopoverContent>
											</Popover>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="paypalCode"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">Paypal Code</FormLabel>
											<FormControl>
												<Input placeholder="Paypal Code" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="rechnungsnummer"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Rechnungsnummer <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Rechnungsnummer" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="discount"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Provision <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Commission" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={invoiceForm.control}
									name="amount"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Umsatz <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Amount"
													{...field}
													value={field.value ?? ''}
													onChange={e => {
														const rawValue = e.target.value
															.replace(/[^\d.,-]/g, '')
															.replace(',', '.')
														field.onChange(rawValue)
													}}
													onBlur={e => {
														const parsed = parseFloat(
															e.target.value.replace(/[^\d.-]/g, '').replace(',', '.'),
														)
														if (!isNaN(parsed)) {
															const formatted = new Intl.NumberFormat('de-DE', {
																style: 'currency',
																currency: 'EUR',
																minimumFractionDigits: 2,
															}).format(parsed)
															field.onChange(formatted)
														}
													}}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<Button
								variant="secondary"
								className="mt-20 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{generatingInvoice && <Loader2 className="animate-spin" />}
								{generatingInvoice ? 'Bitte warten' : 'Rechnung generieren'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				onOpenChange={() => {
					setIsRefreshDetailDialogVisible(false)
					setSelectedUserId(null)
				}}
				open={isRefreshDialogVisible}>
				<ResponsiveModalContent className="gap-0 !overflow-visible !max-w-[550px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Aktualisieren
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Aktualisieren
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<div className="mt-6 lg:mt-8">
						<h2 className="text-muted-foreground text-sm mb-3 lg:mb-4 lg:text-xl font-medium">
							User ID
						</h2>
						<Popover open={open} onOpenChange={setOpen}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									role="combobox"
									className={cn(
										'hover:bg-white w-full overflow-hidden text-ellipsis max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
										!selectedUserId && 'text-muted-foreground',
									)}>
									<p className="max-w-11/12 overflow-hidden text-ellipsis">
										{selectedUserId
											? `ID ${users?.find(user => user.id === selectedUserId)?.id} ${capitalizeFirstLetter(users?.find(user => user.id === selectedUserId)?.first_name ?? '')} ${capitalizeFirstLetter(users?.find(user => user.id === selectedUserId)?.last_name ?? '')} (${users?.find(user => user.id === selectedUserId)?.email})`
											: 'Benutzer auswählen'}
									</p>

									<ChevronDownIcon className="shrink-0 size-4 opacity-50" />
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-[200px] p-0 border-input">
								<Command>
									<CommandInput placeholder="Benutzer suchen..." className="h-9" />
									<CommandList>
										<CommandEmpty>Kein Benutzer gefunden.</CommandEmpty>
										<CommandGroup>
											{Array.isArray(users) &&
												users
													?.filter(user =>
														appliedFilterColumn === 'user_id' && appliedFilterValue
															? user.id === Number(appliedFilterValue)
															: true,
													)
													.map(user => (
														<CommandItem
															value={`ID ${user.id} ${user.first_name} ${user.last_name} (
                                            ${user.email})`}
															key={user.id}
															onSelect={() => {
																setSelectedUserId(user.id)
																setOpen(false)
															}}>
															ID {user.id} {capitalizeFirstLetter(user.first_name)}{' '}
															{capitalizeFirstLetter(user.last_name)} ({user.email})
															<Check
																className={cn(
																	'ml-auto',
																	user.id === selectedUserId ? 'opacity-100' : 'opacity-0',
																)}
															/>
														</CommandItem>
													))}
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>
						<Button
							variant="secondary"
							disabled={!selectedUserId}
							onClick={() =>
								updateCustomerBooking(selectedUserId!, {
									onSuccess: () => {
										refetch()
										setIsRefreshDetailDialogVisible(false)
										setSelectedUserId(null)
									},
									onError: () => {
										toast.error('Failed to refresh')
									},
								})
							}
							className="mt-10 w-full lg:h-[50px] rounded-[12px] font-extrabold text-white">
							{updatingCustomerBooking && <Loader2 className="animate-spin" />}
							{updatingCustomerBooking ? 'Bitte warten' : 'Speichern'}
						</Button>
					</div>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={!!isUserDetailDialogVisible}
				onOpenChange={() => setIsUserDetailDialogVisible(null)}>
				<ResponsiveModalContent className="gap-0 !max-w-[550px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Benutzerdetails
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Benutzerdetails
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<div className="mt-8 lg:mt-12 flex flex-col gap-4 lg:gap-5">
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">ID</p>
							<p className="text-secondary font-extrabold">{isUserDetailDialogVisible?.id}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Vorname</p>
							<p className="text-secondary  capitalize font-extrabold">
								{isUserDetailDialogVisible?.first_name}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Nachname</p>
							<p className="text-secondary font-extrabold capitalize">
								{isUserDetailDialogVisible?.last_name}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">E-Mail</p>
							<p className="text-secondary font-extrabold">{isUserDetailDialogVisible?.email}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Telefon</p>
							<p className="text-secondary font-extrabold">
								{isUserDetailDialogVisible?.phone_number}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Land</p>
							<p className="capitalize text-secondary font-extrabold">
								{isUserDetailDialogVisible?.country}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">PLZ</p>
							<p className="text-secondary font-extrabold">{isUserDetailDialogVisible?.zip}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Stadt</p>
							<p className="capitalize text-secondary font-extrabold">
								{isUserDetailDialogVisible?.country}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Adresse</p>
							<p className="capitalize text-secondary font-extrabold">
								{isUserDetailDialogVisible?.address}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">IBAN</p>
							<p className="text-secondary font-extrabold">
								{formatIban(isUserDetailDialogVisible?.iban)}
							</p>
						</div>
						<div className="flex items-center justify-between">
							<p className="text-secondary/50 font-medium">Erstellt am</p>
							{isUserDetailDialogVisible && (
								<p className="text-secondary font-extrabold">
									{format(
										toZonedTime(new Date(isUserDetailDialogVisible.createdAt), 'UTC'),
										'dd.MM.yyyy, HH:mm',
									)}{' '}
								</p>
							)}
						</div>
					</div>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={isCreateDialogVisible}
				onOpenChange={() => {
					form.reset()
					setIsCreateDialogVisible(false)
				}}>
				<ResponsiveModalContent className="gap-0">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Buchung erstellen
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Create new booking
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="mt-3 lg:mt-8">
								<RadioGroup
									value={form.watch('userType')}
									className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-5"
									onValueChange={value => {
										form.clearErrors()
										form.setValue('userType', value as 'existing' | 'new')
									}}>
									<Label
										htmlFor="r1"
										className={cn(
											'flex border cursor-pointer rounded-2xl text-sm font-medium py-[18px] px-4 items-center space-x-1',
											form.watch('userType') === 'new'
												? 'border-primary text-primary'
												: 'border-muted-foreground text-muted-foreground',
										)}>
										<RadioGroupItem value="new" id="r1" />
										<p>Neuer Nutzer</p>
									</Label>
									<Label
										htmlFor="r2"
										className={cn(
											'flex border cursor-pointer rounded-2xl text-sm font-medium py-[18px] px-4 items-center space-x-1',
											form.watch('userType') === 'existing'
												? 'border-primary text-primary'
												: 'border-muted-foreground text-muted-foreground',
										)}>
										<RadioGroupItem value="existing" id="r2" />
										<p>Bestehender Nutzer</p>
									</Label>
								</RadioGroup>
							</div>
							{form.watch('userType') === 'new' && (
								<div className="mt-6 lg:mt-8">
									<h2 className="text-muted-foreground text-sm lg:text-xl font-medium">
										Benutzerdetails
									</h2>
									<div className="grid grid-cols-1 lg:grid-cols-2 mt-6 gap-y-6 lg:mt-10 lg:gap-x-4 lg:gap-y-5">
										<FormField
											control={form.control}
											name="firstName"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														First Name <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input
															placeholder="First Name"
															{...field}
															value={capitalizeFirstLetter(field.value)}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="lastName"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Last Name <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input
															placeholder="Last Name"
															{...field}
															value={capitalizeFirstLetter(field.value)}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="email"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Email <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input placeholder="Email" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="country"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Ort <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input placeholder="Ort" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="zip"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Postleitzahl <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input placeholder="Postleitzahl" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="iban"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														IBAN <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<InputMask
															{...primaryIbanOptions}
															value={field.value ?? ''}
															onChange={e => field.onChange(e.target.value)}
															placeholder="IBAN"
															component={Input}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="address"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Adresse <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input
															placeholder="Adresse"
															{...field}
															value={capitalizeFirstLetter(field.value)}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="phoneNumber"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Telefonnummer <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input placeholder="Telefonnummer" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<h2 className="text-muted-foreground mt-10 lg:mt-16 text-xs lg:text-xl font-medium">
										Buchungsdetails
									</h2>
									<div className="grid grid-cols-1 lg:grid-cols-2 mt-6 lg:mt-10 lg:gap-x-4 gap-y-6 lg:gap-y-5">
										<FormField
											control={form.control}
											name="package"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Paket <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<Select
														onValueChange={value => {
															form.setValue('start_date', null as any)
															form.setValue('end_date', null as any)
															field.onChange(value)
														}}
														value={field.value}>
														<FormControl>
															<SelectTrigger>
																<SelectValue placeholder="Paket" />
															</SelectTrigger>
														</FormControl>
														<SelectContent>
															{PACKAGES.map(item => (
																<SelectItem key={item.id} value={item.id.toString()}>
																	{item.title} (€ {item.price})
																</SelectItem>
															))}
															<SelectItem value="custom">Benutzerdefiniert</SelectItem>
														</SelectContent>
													</Select>
													<FormMessage />
												</FormItem>
											)}
										/>
										{form.watch('package') && (
											<FormField
												control={form.control}
												name="start_date"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Startdatum <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Popover open={openStartCalendar} onOpenChange={setOpenStartCalendar}>
															<PopoverTrigger asChild>
																<FormControl>
																	<Button
																		variant={'outline'}
																		className={cn(
																			'w-full max-lg:text-xs hover:bg-white px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																			!field.value && 'text-muted-foreground',
																		)}>
																		{field.value ? (
																			format(field.value, 'dd.MM.yyyy')
																		) : (
																			<span>dd.mm.yyyy</span>
																		)}
																		<ChevronDownIcon className="size-4 opacity-50" />
																	</Button>
																</FormControl>
															</PopoverTrigger>
															<PopoverContent className="p-0 border-input" align="start">
																<Calendar
																	mode="single"
																	selected={field.value}
																	onSelect={val => {
																		field.onChange(val)
																		setOpenStartCalendar(false)
																	}}
																	initialFocus
																/>
															</PopoverContent>
														</Popover>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('package') && (
											<FormField
												control={form.control}
												name="end_date"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Enddatum <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Popover open={openEndCalendar} onOpenChange={setOpenEndCalendar}>
															<PopoverTrigger asChild>
																<FormControl>
																	<Button
																		variant={'outline'}
																		disabled={form.watch('package') !== 'custom'}
																		className={cn(
																			'w-full hover:bg-white max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																			!field.value && 'text-muted-foreground',
																		)}>
																		{field.value ? (
																			format(field.value, 'dd.MM.yyyy')
																		) : (
																			<span>dd.mm.yyyy</span>
																		)}
																		<ChevronDownIcon className="size-4 opacity-50" />
																	</Button>
																</FormControl>
															</PopoverTrigger>
															<PopoverContent className="w-auto p-0 border-input" align="start">
																<Calendar
																	mode="single"
																	selected={field.value}
																	onSelect={val => {
																		field.onChange(val)
																		setOpenEndCalendar(false)
																	}}
																	disabled={date => date < form.watch('start_date')}
																	initialFocus
																/>
															</PopoverContent>
														</Popover>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{startDate && endDate && (
											<FormField
												control={form.control}
												name="shelf"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Regal Nummer <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Select onValueChange={field.onChange} value={field.value}>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder="Paket" />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{standList &&
																	standList.length > 0 &&
																	standList
																		.filter(
																			stand => stand.is_available && stand.status === 'active',
																		)
																		.map(stand => (
																			<SelectItem
																				key={'stand_' + stand.id}
																				value={stand.id.toString()}>
																				{stand.label}
																			</SelectItem>
																		))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('package') === 'custom' && (
											<FormField
												control={form.control}
												name="rent_price"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Preis <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<FormControl>
															<Input
																placeholder="Preis"
																inputMode="numeric"
																{...field}
																onChange={e => {
																	const value = e.target.value
																	if (/^\d*\.?\d*$/.test(value)) {
																		field.onChange(value)
																	}
																}}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('shelf') && (
											<FormField
												control={form.control}
												name="additionalServices"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Zusatzleistungen{' '}
															<span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Select onValueChange={field.onChange} value={field.value}>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder="Paket" />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{EXTRAS.map(extra => (
																	<SelectItem key={extra.id} value={extra.id.toString()}>
																		{extra.title} (€ {extra.price})
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
									</div>
								</div>
							)}
							{form.watch('userType') === 'existing' && (
								<div className="mt-6 lg:mt-8">
									<h2 className="text-muted-foreground text-sm lg:text-xl font-medium">
										Benutzerdetails
									</h2>
									<div className="mt-6 lg:mt-10 ">
										<FormField
											control={form.control}
											name="existingUser"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Benutzer auswählen{' '}
														<span className="text-destructive-foreground">*</span>
													</FormLabel>
													<Popover open={open} onOpenChange={setOpen}>
														<PopoverTrigger asChild>
															<FormControl>
																<Button
																	variant="outline"
																	role="combobox"
																	className={cn(
																		'hover:bg-white overflow-hidden text-ellipsis max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																		!field.value && 'text-muted-foreground',
																	)}>
																	<p className="max-w-11/12 overflow-hidden text-ellipsis">
																		{field.value
																			? `ID ${users?.find(user => user.id === Number(field.value))?.id} ${capitalizeFirstLetter(users?.find(user => user.id === Number(field.value))?.first_name ?? '')} ${capitalizeFirstLetter(users?.find(user => user.id === Number(field.value))?.last_name ?? '')} (${users?.find(user => user.id === Number(field.value))?.email})`
																			: 'Benutzer auswählen'}
																	</p>

																	<ChevronDownIcon className="shrink-0 size-4 opacity-50" />
																</Button>
															</FormControl>
														</PopoverTrigger>
														<PopoverContent className="w-[200px] p-0 border-input">
															<Command>
																<CommandInput placeholder="Benutzer suchen..." className="h-9" />
																<CommandList>
																	<CommandEmpty>Kein Benutzer gefunden.</CommandEmpty>
																	<CommandGroup>
																		{Array.isArray(users) &&
																			users?.map(user => (
																				<CommandItem
																					value={`ID ${user.id} ${user.first_name} ${user.last_name} (
                                            ${user.email})`}
																					key={user.id}
																					onSelect={() => {
																						form.setValue('existingUser', user.id.toString(), {
																							shouldValidate: true,
																						})
																						setOpen(false)
																					}}>
																					ID {user.id} {capitalizeFirstLetter(user.first_name)}{' '}
																					{capitalizeFirstLetter(user.last_name)} ({user.email})
																					<Check
																						className={cn(
																							'ml-auto',
																							user.id.toString() === field.value
																								? 'opacity-100'
																								: 'opacity-0',
																						)}
																					/>
																				</CommandItem>
																			))}
																	</CommandGroup>
																</CommandList>
															</Command>
														</PopoverContent>
													</Popover>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<h2 className="text-muted-foreground mt-10 lg:mt-16 text-xs lg:text-xl font-medium">
										Buchungsdetails
									</h2>
									<div className="grid grid-cols-1 lg:grid-cols-2 mt-6 lg:mt-10 lg:gap-x-4 gap-y-6 lg:gap-y-5">
										<FormField
											control={form.control}
											name="package"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Paket <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<Select
														onValueChange={value => {
															form.setValue('start_date', null as any)
															form.setValue('end_date', null as any)
															field.onChange(value)
														}}
														value={field.value}>
														<FormControl>
															<SelectTrigger>
																<SelectValue placeholder="Paket" />
															</SelectTrigger>
														</FormControl>
														<SelectContent>
															{PACKAGES.map(item => (
																<SelectItem key={item.id} value={item.id.toString()}>
																	{item.title} (€ {item.price})
																</SelectItem>
															))}
															<SelectItem value="custom">Benutzerdefiniert</SelectItem>
														</SelectContent>
													</Select>
													<FormMessage />
												</FormItem>
											)}
										/>
										{form.watch('package') && (
											<FormField
												control={form.control}
												name="start_date"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Startdatum <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Popover open={openStartCalendar} onOpenChange={setOpenStartCalendar}>
															<PopoverTrigger asChild>
																<FormControl>
																	<Button
																		variant={'outline'}
																		className={cn(
																			'w-full max-lg:text-xs hover:bg-white px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																			!field.value && 'text-muted-foreground',
																		)}>
																		{field.value ? (
																			format(field.value, 'dd.MM.yyyy')
																		) : (
																			<span>dd.mm.yyyy</span>
																		)}
																		<ChevronDownIcon className="size-4 opacity-50" />
																	</Button>
																</FormControl>
															</PopoverTrigger>
															<PopoverContent className="p-0 border-input" align="start">
																<Calendar
																	mode="single"
																	selected={field.value}
																	onSelect={val => {
																		field.onChange(val)
																		setOpenStartCalendar(false)
																	}}
																	initialFocus
																/>
															</PopoverContent>
														</Popover>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('package') && (
											<FormField
												control={form.control}
												name="end_date"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Enddatum <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Popover open={openEndCalendar} onOpenChange={setOpenEndCalendar}>
															<PopoverTrigger asChild>
																<FormControl>
																	<Button
																		variant={'outline'}
																		disabled={form.watch('package') !== 'custom'}
																		className={cn(
																			'w-full hover:bg-white max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																			!field.value && 'text-muted-foreground',
																		)}>
																		{field.value ? (
																			format(field.value, 'dd.MM.yyyy')
																		) : (
																			<span>dd.mm.yyyy</span>
																		)}
																		<ChevronDownIcon className="size-4 opacity-50" />
																	</Button>
																</FormControl>
															</PopoverTrigger>
															<PopoverContent className="w-auto p-0 border-input" align="start">
																<Calendar
																	mode="single"
																	selected={field.value}
																	onSelect={val => {
																		field.onChange(val)
																		setOpenEndCalendar(false)
																	}}
																	disabled={date => date < form.watch('start_date')}
																	initialFocus
																/>
															</PopoverContent>
														</Popover>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{startDate && endDate && (
											<FormField
												control={form.control}
												name="shelf"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Regal Nummer <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Select onValueChange={field.onChange} value={field.value}>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder="Paket" />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{standList &&
																	standList.length > 0 &&
																	standList
																		.filter(
																			stand => stand.is_available && stand.status === 'active',
																		)
																		.map(stand => (
																			<SelectItem
																				key={'stand_' + stand.id}
																				value={stand.id.toString()}>
																				{stand.label}
																			</SelectItem>
																		))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('package') === 'custom' && (
											<FormField
												control={form.control}
												name="rent_price"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Preis <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<FormControl>
															<Input
																placeholder="Preis"
																{...field}
																onChange={e => {
																	const value = e.target.value
																	if (/^\d*\.?\d*$/.test(value)) {
																		field.onChange(value)
																	}
																}}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('shelf') && (
											<FormField
												control={form.control}
												name="additionalServices"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Zusatzleistungen{' '}
															<span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Select onValueChange={field.onChange} value={field.value}>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder="Paket" />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{EXTRAS.map(extra => (
																	<SelectItem key={extra.id} value={extra.id.toString()}>
																		{extra.title} (€ {extra.price})
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
									</div>
								</div>
							)}
							<Button
								variant="secondary"
								className="mt-20 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{placingBooking && <Loader2 className="animate-spin" />}
								{placingBooking ? 'Bitte warten' : 'Speichern'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<div className="lg:hidden rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
						<TaskSquareIcon className="text-secondary" />
					</div>
					<h1 className="text-muted-foreground lg:text-secondary text-xs lg:text-2xl font-medium lg:font-extrabold">
						Rechnungen
					</h1>
				</div>
				<div className="flex items-center gap-2">
					<Button
						size="icon"
						variant="secondary"
						onClick={() => {
							if (isSingleUser) {
								updateCustomerBooking(filteredBookings[0].user_id)
							} else {
								setIsRefreshDetailDialogVisible(true)
							}
						}}
						className="size-8 lg:hidden rounded-lg bg-secondary">
						{updatingCustomerBooking ? (
							<Loader2 className="animate-spin" />
						) : (
							<ArrowPathIcon className="size-5 lg:size-6 text-white" />
						)}
					</Button>
					<Button
						size="icon"
						variant="secondary"
						onClick={() => setIsCreateDialogVisible(true)}
						className="size-8 lg:size-14 rounded-lg lg:rounded-xl bg-secondary">
						<PlusIcon className="size-5 lg:size-6 text-white" />
					</Button>
				</div>
			</div>
			<div className="flex max-lg:mt-5 mt-6 flex-col gap-3 lg:gap-4">
				<div className="grid grid-cols-2 items-center gap-[9px] lg:hidden">
					<div className="relative flex items-center">
						<Input
							value={searchValue}
							onChange={e => setSearchValue(e.target.value)}
							className="!pl-[34px] h-10 text-xs"
							placeholder="Suche..."
						/>
						<MagnifyingGlassIcon className="size-4 text-muted-foreground absolute left-2" />
					</div>
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => {
							setPage(1)
							setRowsPerPage(Number(value))
						}}>
						<SelectTrigger className="w-full h-10 text-xs">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="lg:hidden flex flex-col gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="h-10 text-xs w-full">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<div className="flex lg:hidden items-center gap-2">
						<Input
							value={filterValue}
							onChange={e => setFilterValue(e.target.value)}
							className="h-10 text-xs"
							placeholder="Bezeichnung eingeben"
						/>
						<Button
							onClick={handleApplyFilter}
							disabled={updatingCustomerBooking}
							variant="secondary"
							className="w-[73px] py-3 text-xs font-medium bg-secondary rounded-lg">
							Suche
						</Button>
					</div>
				</div>
				<div className="flex max-lg:hidden items-center gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="flex-1">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Input
						value={filterValue}
						onChange={e => setFilterValue(e.target.value)}
						className="flex-1"
						placeholder="Bezeichnung eingeben"
					/>
					<Button
						onClick={handleApplyFilter}
						disabled={updatingCustomerBooking}
						variant="secondary"
						className="w-[90px] font-medium bg-secondary rounded-xl">
						Suche
					</Button>
				</div>
			</div>
			<div className="flex max-lg:hidden items-center justify-between mt-6">
				<div className="flex items-center gap-4">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => {
							setPage(1)
							setRowsPerPage(Number(value))
						}}>
						<SelectTrigger className="w-[127px]">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-8">
					<Button
						onClick={() => {
							if (isSingleUser) {
								updateCustomerBooking(filteredBookings[0].user_id)
							} else {
								setIsRefreshDetailDialogVisible(true)
							}
						}}
						variant="default"
						className="rounded-xl text-white py-3">
						{updatingCustomerBooking ? <Loader2 className="animate-spin" /> : 'Aktualisieren'}
					</Button>
					<PaginationComponent
						currentPage={page}
						totalPages={data?.pagination?.totalPages ?? 1}
						onPageChange={setPage}
					/>
					<div className="relative flex items-center">
						<Input
							value={searchValue}
							onChange={e => setSearchValue(e.target.value)}
							className="!pl-[38px]"
							placeholder="Suche..."
						/>
						<MagnifyingGlassIcon className="size-5 text-muted-foreground absolute left-2" />
					</div>
				</div>
			</div>
			<div className="mt-4">
				{isSmallScreen ? (
					<MobileBookingsTable />
				) : (
					<DataTable
						columns={columns}
						canToggleVisibility
						data={filteredBookings ?? []}
						isLoading={isLoading}
					/>
				)}
			</div>
			<div className="mt-6 lg:mt-16 flex justify-center">
				<PaginationComponent
					currentPage={page}
					totalPages={data?.pagination.totalPages ?? 1}
					onPageChange={setPage}
				/>
			</div>
		</div>
	)
}
